import {useCallback, useSyncExternalStore} from "react";

// Clipboard di sessione per copiare il valore di un campo (es. l'elenco tag di un multi-select
// come Meccanica, o il valore singolo di un select come Categoria) da una card e incollarlo su
// un'altra, senza riselezionarlo ogni volta. Store esterno a livello di modulo (non un Context)
// perché il valore deve sopravvivere anche a card che si smontano/rimontano nella lista;
// persistito anche in sessionStorage per resistere a un reload della pagina nella stessa sessione
// browser. Un array viene clonato e trattato come tag multipli; qualunque altro valore (stringa,
// null) è trattato come campo a selezione singola.
const STORAGE_PREFIX = "flyerTagClipboard:";
const listeners = new Set();
const cache = {};

function loadFromStorage(fieldKey)
{
	try
	{
		const raw = sessionStorage.getItem(STORAGE_PREFIX + fieldKey);
		return raw ? JSON.parse(raw) : null;
	}
	catch
	{
		return null;
	}
}

// Il valore va cacheato: senza cache, ogni lettura rifarebbe il parse di sessionStorage
// restituendo un nuovo array (nuovo riferimento) anche a contenuto invariato, e
// useSyncExternalStore lo tratterebbe come "cambiato" ad ogni render → loop di re-render.
export function getTagClipboard(fieldKey)
{
	if (!(fieldKey in cache))
		cache[fieldKey] = loadFromStorage(fieldKey);
	return cache[fieldKey];
}

export function setTagClipboard(fieldKey, value)
{
	const next = Array.isArray(value) ? [...value] : (value ?? null);
	cache[fieldKey] = next;
	try
	{
		sessionStorage.setItem(STORAGE_PREFIX + fieldKey, JSON.stringify(next));
	}
	catch
	{
		// sessionStorage non disponibile (es. modalità privata): il copia/incolla resta comunque
		// valido in memoria per il resto della sessione React corrente.
	}
	listeners.forEach((listener) => listener());
}

function subscribeTagClipboard(listener)
{
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function useTagFieldClipboard(fieldKey)
{
	const value = useSyncExternalStore(subscribeTagClipboard, () => getTagClipboard(fieldKey));
	const copy = useCallback((next) => setTagClipboard(fieldKey, next), [fieldKey]);
	const hasValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== "";
	return {value, copy, hasValue};
}

// Clipboard generico per campi di testo libero (es. Note, Descrizione estesa, o qualunque campo
// TEXTAREA creato su misura per una cooperativa in Settings): a differenza dei tag qui sopra,
// l'elenco di questi campi non è fisso/noto in anticipo, quindi non si può chiamare un hook per
// ogni campo (violerebbe le regole degli hook, chiamate condizionalmente in un loop variabile).
// Un solo slot "ultimo campo copiato" invece di uno slot per campo: la card che lo legge confronta
// la propria fieldKey con quella copiata e abilita "Incolla" solo se coincidono, cosi il testo di
// un campo non finisce mai per sbaglio in un campo diverso.
const GENERIC_STORAGE_KEY = "flyerFieldClipboard:generic";
const genericListeners = new Set();
let genericCache;

function loadGenericFromStorage()
{
	try
	{
		const raw = sessionStorage.getItem(GENERIC_STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	}
	catch
	{
		return null;
	}
}

function getGenericClipboard()
{
	if (genericCache === undefined) genericCache = loadGenericFromStorage();
	return genericCache;
}

function setGenericClipboard(fieldKey, value)
{
	const next = {fieldKey, value: value ?? ""};
	genericCache = next;
	try
	{
		sessionStorage.setItem(GENERIC_STORAGE_KEY, JSON.stringify(next));
	}
	catch
	{
		// sessionStorage non disponibile: resta comunque valido in memoria per la sessione corrente.
	}
	genericListeners.forEach((listener) => listener());
}

function subscribeGenericClipboard(listener)
{
	genericListeners.add(listener);
	return () => genericListeners.delete(listener);
}

// Chiamare UNA VOLTA a livello top del componente (non dentro un loop/callback per campo): il
// risultato va poi letto/usato per ogni campo testo incontrato durante il render.
export function useGenericFieldClipboard()
{
	const entry = useSyncExternalStore(subscribeGenericClipboard, getGenericClipboard);
	const copy = useCallback((fieldKey, value) => setGenericClipboard(fieldKey, value), []);
	const canPasteInto = useCallback(
		(fieldKey) => !!entry && entry.fieldKey === fieldKey && entry.value !== "",
		[entry]
	);
	return {entry, copy, canPasteInto};
}
